import type { Run } from '@/types/database'
import { ArrowLeft, ChevronRight, Layers, WifiOff } from 'lucide-react'
import { RunStatusBadge } from '@/components/RunSelector'
import WidgetRetry from '@/components/WidgetRetry'
import { BrandLoader } from '@/components/BrandLoader'
import PageHeader from '@/components/PageHeader'
import { sastHM, sastTodayYMD, sastYMD } from '@/lib/dates'

/** What waits on one run, for the opt-in card line. */
export interface RunWaiting { deliveries: number; bins: number }
/** One read for all the listed runs. 'loading' / 'error' show a dash, never a 0. */
export interface RunWaitingRead { state: 'loading' | 'error' | 'ok'; byRun: Record<string, RunWaiting> }

function startedText(iso: string): string | null {
  const hm = sastHM(iso)
  if (!hm) return null
  const ymd = sastYMD(Date.parse(iso))
  return ymd === sastTodayYMD() ? hm : `${ymd} ${hm}`
}

// L1 view for pages where the operator must explicitly pick a run before
// they can act. Replaces the auto-selecting RunSelector dropdown — operators
// kept tipping / packing / rejecting onto the wrong run because the dropdown
// silently fell back to the most recent ACTIVE run when their device first
// loaded. Now every entry to those pages requires a tap.
//
// The picked-run state lives in the parent page (it usually clears
// page-specific extras too — selected delivery, view='new', etc.). This
// component only renders the list.
interface Props {
  title:       string
  runs:        Run[]
  isLoading:   boolean
  /** The runs query failed. Without this a shed-wifi blip renders emptyTitle
      ("No runs are currently tipping") as fact on the front door of the page. */
  isError?:    boolean
  /** Refetch for the error state's Retry. */
  onRetry?:    () => void
  onBack:      () => void
  onPick:      (runId: string) => void
  emptyTitle:  string
  emptyHint:   string
  pickHint?:   string
  /** Optional content rendered to the right of the title (e.g. an action) */
  headerRight?: React.ReactNode
  /** Optional content above the run list that does not depend on it — the
      Pallets landing's "Find a pallet — type or scan" (F09 UF-03). It shows
      while the runs load and when their read fails: finding a pallet needs
      no run. */
  aboveList?:   React.ReactNode
  /** Hide the status pill on each card. For a list already filtered to one
      status (Tipping lists ACTIVE runs only), where the pill never differs
      between cards (U12 UF-08). Off by default: Pallets, QC, Rejects keep it. */
  hideStatus?:  boolean
  /** Hide the packout % on each card. Packout is a pack-line figure; at the
      tipper it reads as a fault when amber (U12 UF-09). Off by default. */
  hidePackout?: boolean
  /** Opt-in (F02 UF-16): a line on each card with what waits to tip on that run. */
  waiting?: RunWaitingRead
  /** Opt-in (F02 UF-16): "Started 08:12" on each card. */
  showStarted?: boolean
  /** Opt-in (F02 UF-16): a line above the list, e.g. why the station dropped here. */
  notice?: string | null
  /** Opt-in (F02 UF-16): the house PageHeader (Help, Go to) instead of the hand-rolled bar. */
  houseHeader?: boolean
  /** Opt-in (F02 UF-03): a button under the empty hint, e.g. "Start a run". The page
      gates it; pass nothing for a role that cannot act (put who can in emptyHint). */
  emptyAction?: React.ReactNode
}

export default function PickRunFirst({
  title, runs, isLoading, isError = false, onRetry, onBack, onPick,
  emptyTitle, emptyHint, pickHint = 'Pick a run', headerRight, aboveList,
  hideStatus = false, hidePackout = false, waiting, showStarted, notice, houseHeader, emptyAction,
}: Props) {
  return (
    <div className="min-h-full surface-sunk flex flex-col">
      <RunListHeader houseHeader={houseHeader} onBack={onBack} title={title} headerRight={headerRight} />

      <div className="flex-1 p-4 space-y-3">
        {aboveList}

        <RunListBody notice={notice} isLoading={isLoading} isError={isError} runs={runs} onRetry={onRetry}
          emptyTitle={emptyTitle} emptyHint={emptyHint} emptyAction={emptyAction} pickHint={pickHint}
          onPick={onPick} hideStatus={hideStatus} hidePackout={hidePackout} showStarted={showStarted} waiting={waiting} />
      </div>
    </div>
  )
}

function RunListBody({
  notice, isLoading, isError, runs, onRetry, emptyTitle, emptyHint, emptyAction, pickHint, ...card
}: {
  notice?: string | null; isLoading: boolean; isError: boolean; runs: Run[]; onRetry?: () => void
  emptyTitle: string; emptyHint: string; emptyAction?: React.ReactNode; pickHint: string
  onPick: (runId: string) => void; hideStatus: boolean; hidePackout: boolean; showStarted?: boolean; waiting?: RunWaitingRead
}) {
  return (
    <>
    {notice && (
      <p role="status" className="rounded-xl border hairline surface-raised px-3 py-2 text-sm text-fg">{notice}</p>
    )}

    {isLoading ? (
      <div className="flex justify-center py-12">
        <BrandLoader className="w-8 h-8" />
      </div>
    ) : runs.length > 0 ? (
      <RunCards runs={runs} pickHint={pickHint} {...card} />
    ) : isError ? (
      // Failed read is not empty: "No runs" on a failed fetch is a lie the operator acts on.
      <LoadFailed onRetry={onRetry} />
    ) : (
      <NoRuns title={emptyTitle} hint={emptyHint} action={emptyAction} />
    )}
    </>
  )
}

function RunCards({ runs, pickHint, ...card }: {
  runs: Run[]; pickHint: string; onPick: (runId: string) => void; hideStatus: boolean
  hidePackout: boolean; showStarted?: boolean; waiting?: RunWaitingRead
}) {
  return (
    <>
      <p className="text-xs font-semibold text-fg-muted uppercase tracking-wider px-1">
        {pickHint}
      </p>
      {runs.map(r => <RunCard key={r.run_id} r={r} {...card} />)}
    </>
  )
}

function LoadFailed({ onRetry }: { onRetry?: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-fg-muted text-center">
      <WifiOff size={48} className="mb-3 opacity-40" />
      <p className="font-medium text-fg-muted">Couldn't load runs</p>
      <p className="text-sm mt-1 max-w-xs">Check the connection — the list may not be empty.</p>
      {onRetry && <WidgetRetry onRetry={onRetry} className="mt-3" />}
    </div>
  )
}

function NoRuns({ title, hint, action }: { title: string; hint: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-fg-muted text-center">
      <Layers size={48} className="mb-3 opacity-40" />
      <p className="font-medium">{title}</p>
      <p className="text-sm mt-1 max-w-xs">{hint}</p>
      {action && <div className="mt-3">{action}</div>}
    </div>
  )
}

function RunListHeader({ houseHeader, onBack, title, headerRight }: {
  houseHeader?: boolean; onBack: () => void; title: string; headerRight?: React.ReactNode
}) {
  if (houseHeader) return <PageHeader onBack={onBack} title={title} actions={headerRight} />
  return (
    <header className="sticky top-0 z-10 surface-sunk border-b hairline px-4 py-3 flex items-center gap-3">
      <button onClick={onBack} aria-label="Back" className="tap-target p-2 -ml-2 rounded-xl hover:bg-surface">
        <ArrowLeft size={20} />
      </button>
      <h1 className="text-lg font-bold flex-1">{title}</h1>
      {headerRight}
    </header>
  )
}

function RunCard({ r, onPick, hideStatus, hidePackout, showStarted, waiting }: {
  r: Run; onPick: (runId: string) => void; hideStatus: boolean; hidePackout: boolean
  showStarted?: boolean; waiting?: RunWaitingRead
}) {
  const started = showStarted ? startedText(r.started_at) : null
  return (
    <button
      onClick={() => onPick(r.run_id)}
      className="card w-full text-left active:scale-[0.98] transition-transform space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-fg">{r.run_number}</p>
            {!hideStatus && <RunStatusBadge status={r.status} size="xs" />}
          </div>
          <p className="text-sm text-fg-muted truncate">
            {r.grower} · {r.variety}
            {r.puc && <> · <span className="font-mono">{r.puc}</span></>}
          </p>
        </div>
        <ChevronRight size={20} className="text-fg-muted shrink-0 mt-1" />
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-fg-muted">
        <span>{r.tipped_net_weight_kg.toFixed(0)} kg tipped</span>
        <span>{r.pallet_count} pallets</span>
        {started && <span>Started {started}</span>}
        {waiting && <span>{waitingText(waiting, r.run_id)}</span>}
        {!hidePackout && r.packout_percent != null && (
          <span className={`font-semibold ${r.packout_percent < 85 ? 'text-amber-400' : 'text-green-400'}`}>
            {r.packout_percent.toFixed(1)}% packout
          </span>
        )}
      </div>
    </button>
  )
}

function waitingText(w: RunWaitingRead, runId: string): string {
  if (w.state !== 'ok') return w.state === 'error' ? 'Waiting: — (could not read)' : 'Waiting: —'
  const x = w.byRun[runId] ?? { deliveries: 0, bins: 0 }
  return `${x.deliveries} ${x.deliveries === 1 ? 'delivery' : 'deliveries'} · ${x.bins} ${x.bins === 1 ? 'bin' : 'bins'} waiting`
}
